import { guardLoop } from './guard'
import { parse } from './json5'
import {
  compileLiteral,
  compileLiteralPart,
  isEmptyNode,
  isValuableNode
} from './utils'

import type { AnyNode, Mustache } from 'acorn'

export interface CompileOptions {
  reportName?: string
  stringMode?: boolean
  withTryCatch?: boolean
}

const internalCompile = (node: Mustache, options?: CompileOptions) => {
  const withTryCatch = options?.withTryCatch ?? false

  const reportName = options?.reportName ?? 'reportError'

  const isStringMode = options?.stringMode === true

  function tryCatch(expression: string, node: AnyNode) {
    if (!withTryCatch) return expression
    if (node.type === 'Literal') return expression
    return `(function(){try{return (${expression})}catch(e){${reportName}(e,${JSON.stringify(node.range)});${isStringMode ? `return ''` : ''}}})()`
  }

  function template(exp: string, fallback = false) {
    return isStringMode ? `\${(${exp})${fallback ? ' ?? ""' : ''}}` : exp
  }

  function json5() {
    try {
      const input = node.source
      const shape = node.body
        .map((child, index) =>
          child.type === 'MustacheLiteral'
            ? input.slice(child.start, child.end)
            : `{#${index}}`
        )
        .join('')

      const wrapper = (expression: string, node: AnyNode, type: string) => {
        const exp = tryCatch(expression, node)
        if (isStringMode) {
          switch (type) {
            case 'SlotArrayElement':
            case 'SlotObjectProperty': {
              return node.type === 'Literal'
                ? exp
                : template(`JSON.stringify(${exp})`)
            }
            case 'SlotString': {
              return template(exp, true)
            }
            default: {
              return template(exp)
            }
          }
        } else {
          return exp
        }
      }

      return parse(shape, {
        slot: (index, type, quote) => {
          const child = node.body[index]
          const source = input.slice(child.start, child.end)
          const exp = wrapper ? wrapper(source, node.body[index], type) : source
          switch (type) {
            case 'SlotObjectKey': {
              if (isStringMode) return `"${exp}"`
              return `[${exp}]`
            }
            case 'SlotString': {
              if (isStringMode) return exp
              if (child.type === 'Literal') return `${quote}+${exp}+${quote}`
              return `${quote}+((${exp}) ?? "")+${quote}`
            }
          }
          return exp
        },
        stringMode: isStringMode
      })
    } catch {}
  }

  const literal = json5()
  if (literal) {
    return literal
  }

  if (node.body.length === 0) {
    return isStringMode ? '""' : 'void 0'
  }

  if (node.body.length === 1) {
    const child = node.body[0]
    if (child.type === 'MustacheLiteral') {
      return compileLiteral(child)
    } else {
      let result = ''
      if (withTryCatch) {
        result += `(function(){try{return (`
      }
      result += node.source.slice(child.start, child.end)
      if (withTryCatch) {
        result += `)}catch(e){${reportName}(e);return}})()`
      }
      if (isStringMode) return `\`\${(${result}) ?? ""}\``
      return result
    }
  }

  let result = '`'

  for (let i = 0; i < node.body.length; i++) {
    const child = node.body[i]
    if (isEmptyNode(child)) continue
    if (child.type === 'MustacheLiteral') {
      result += compileLiteralPart(child.raw ?? '')
    } else {
      if (child.type === 'Literal') {
        result += String(child.value)
        continue
      }
      result += withTryCatch ? '${(function(){try{return (' : '${'
      result += `(${node.source.slice(child.start, child.end)})`
      if (!isValuableNode(child)) result += ` ?? ""`
      result += withTryCatch
        ? `)}catch(e){${reportName}(e,${JSON.stringify(child.range)});return ''}})()}`
        : '}'
    }
  }
  result += '`'

  return result
}

export function compile(node: Mustache, options?: CompileOptions) {
  return guardLoop(internalCompile(node, options))
}
